/**
 * @fileoverview GoldButton — Premium light sweep efektli altın CTA butonu.
 * variant: 'solid' | 'outline'
 * size:    'sm' | 'md' | 'lg'
 */

import { useRef } from 'react'
import { cn } from '../../utils/cn'
import { useGoldHover } from '../../hooks/useGoldHover'

/** @type {Record<string, Record<string, string>>} */
const SIZES = {
  sm: 'px-4 py-2 text-xs',
  md: 'px-6 py-3 text-sm',
  lg: 'px-8 py-4 text-base',
}

/**
 * @param {{
 *   children:   React.ReactNode,
 *   onClick?:   () => void,
 *   href?:      string,
 *   type?:      'button' | 'submit' | 'reset',
 *   size?:      'sm' | 'md' | 'lg',
 *   variant?:   'solid' | 'outline',
 *   disabled?:  boolean,
 *   className?: string,
 *   id?:        string,
 * }} props
 */
export default function GoldButton({
  children,
  onClick,
  href,
  type     = 'button',
  size     = 'md',
  variant  = 'solid',
  disabled = false,
  className,
  id,
}) {
  const btnRef = useRef(null)
  const { shineRef } = useGoldHover(btnRef, { disabled })

  const baseClass = cn(
    // Layout & typography
    'relative overflow-hidden inline-flex items-center justify-center gap-2',
    'font-semibold tracking-wider uppercase rounded-sm',
    'transition-all duration-300 select-none',
    // Size
    SIZES[size] ?? SIZES.md,
    // State
    disabled
      ? 'opacity-50 cursor-not-allowed pointer-events-none'
      : 'hover:scale-[1.03] active:scale-95 hover:shadow-[0_0_28px_rgba(191,149,63,0.35)]',
    // Variant overrides via className last
    className
  )

  const solidStyle = {
    background: 'linear-gradient(135deg, #BF953F 0%, #FCF6BA 40%, #B38728 60%, #FBF5B7 80%, #AA771C 100%)',
    color: '#1A1A1A',
  }

  const outlineStyle = {
    background: 'transparent',
    border: '1px solid transparent',
    backgroundImage:
      'linear-gradient(#0D172A, #0D172A), linear-gradient(135deg, #BF953F, #FCF6BA, #AA771C)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    color: '#FCF6BA',
  }

  const sharedProps = {
    id,
    ref:             btnRef,
    disabled,
    'aria-disabled': disabled,
    style:           variant === 'solid' ? solidStyle : outlineStyle,
    className:       baseClass,
  }

  const content = (
    <>
      {/* Shine overlay — shifted by useGoldHover */}
      <span
        ref={shineRef}
        className="absolute inset-0 bg-white/25 skew-x-[-12deg] z-0 pointer-events-none"
        aria-hidden="true"
      />
      {/* Content above shine */}
      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </>
  )

  if (href && !disabled) {
    return (
      <a href={href} {...sharedProps}>
        {content}
      </a>
    )
  }

  return (
    <button type={type} onClick={onClick} {...sharedProps}>
      {content}
    </button>
  )
}
